import { useEffect, useRef } from "react";
import {
  useEstimatePaymentFee,
  useWithdrawalState,
  useWithdrawalActions,
  useWithdrawalValidation,
  useCalculatedLimits,
} from "src/hooks";
import { PaymentType, ToggleSwitch, VerificationType, PaymentsRoutesResponse } from "src/api";
import { useDepositWithdrawContext } from "src/contexts";
import { EWithdrawalStep } from "src/types";

export interface WithdrawalStep {
  currentStep: EWithdrawalStep;
  stepLoading: boolean;
  stepError: string | null;
}

export const useWithdrawal = ({ paymentsRoutes }: { paymentsRoutes: PaymentsRoutesResponse }) => {
  // Get context data
  const {
    balance,
    user: { mfa_status },
  } = useDepositWithdrawContext();
  const { currency } = balance;

  // State management
  const state = useWithdrawalState();
  const {
    amount,
    currentStep,
    isLoading,
    error,
    banksList,
    selectedBank,
    accountNumber,
    sessionData,
    mfaCode,
    setMfaCode,
    isRtcEnabled,
  } = state;

  // Payment fee estimation
  const { paymentFee, paymentFeeLoading, paymentFeeError } = useEstimatePaymentFee({
    paymentType: PaymentType.Withdrawal,
    amount: Number(amount),
  });

  // Live per-period used/limit/remaining (in wallet currency) for the user's
  // limit group. `availableLimit` is the tightest headroom across the
  // per-currency and aggregated caps (Infinity = no cap) and gates submit.
  const {
    limitsStatus,
    availableLimit,
    loading: limitsLoading,
    refetch: refetchLimits,
  } = useCalculatedLimits(PaymentType.Withdrawal);

  // Returning to the amount screen from a later step re-reads the limits —
  // the used totals may have moved server-side while the user was away.
  const previousStep = useRef(currentStep);
  useEffect(() => {
    if (currentStep === EWithdrawalStep.AmountInput && previousStep.current !== EWithdrawalStep.AmountInput) {
      refetchLimits();
    }
    previousStep.current = currentStep;
  }, [currentStep, refetchLimits]);

  // Validation
  const { errors } = useWithdrawalValidation({
    amount,
    paymentFeeError,
    paymentFee,
    availableLimit,
  });

  // Actions
  const actions = useWithdrawalActions({
    amount,
    setAmount: state.setAmount,
    setIsLoading: state.setIsLoading,
    setError: state.setError,
    setCurrentStep: state.setCurrentStep,
    setSessionData: state.setSessionData,
    setBanksList: state.setBanksList,
    setSelectedBank: state.setSelectedBank,
    setAccountNumber: state.setAccountNumber,
    sessionData,
    selectedBank,
    accountNumber,
    banksList,
    currencyId: currency.currency_id,
    currencyPrecision: currency.precision,
    mfaCode,
    isRtcEnabled,
    setIsRtcEnabled: state.setIsRtcEnabled,
  });

  // Compose withdrawal step info
  const withdrawalStep: WithdrawalStep = {
    currentStep,
    stepLoading: isLoading,
    stepError: error,
  };

  const userUseMfa = mfa_status === ToggleSwitch.On;
  const isMfaRequired = paymentsRoutes?.verification_type === VerificationType.Totp;

  return {
    // Amount
    amount,

    // Payment fee
    paymentFee,
    paymentFeeLoading,

    // Balance
    balance,

    // Platform limit group (used/limit/remaining, in wallet currency)
    limitsStatus,
    availableLimit,
    limitsLoading,

    // Validation
    errors,

    // Actions
    handleAmountChange: actions.handleAmountChange,
    handleStepChange: actions.handleStepChange,
    handleBankSelection: actions.handleBankSelection,
    handleRtcChange: actions.handleRtcChange,

    // Step state
    withdrawalStep,

    // Bank data
    banksList,
    selectedBank,
    accountNumber,

    // MFA
    mfaCode,
    setMfaCode,
    userUseMfa,
    isMfaRequired,

    // RTC
    isRtcEnabled,
  };
};
